import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, TrendingUp, MessageSquare, ChevronRight, Plus, Star, Globe, Lock } from 'lucide-react';

/**
 * RELEVANT GROUPS RECOMMENDATION
 * Shows groups related to user's watchlist stocks
 * Can be used on Dashboard or Groups page
 */
const RelevantGroupsRecommendation = ({ groups = [], activeStock = null, onActiveStockChange, onViewAll }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Filter groups by active stock if one is selected
  const displayedGroups = activeStock
    ? groups.filter(g => g.tickers?.includes(activeStock))
    : groups;

  // Clicking a ticker chip toggles it: same ticker again clears the filter,
  // a different ticker switches to it. #40's fix -- the filter logic above
  // already existed, it just had no UI ever calling onActiveStockChange.
  const handleTickerClick = (ticker) => {
    if (!onActiveStockChange) return;
    onActiveStockChange(ticker === activeStock ? null : ticker);
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/groupies');
    }
  };

  if (!displayedGroups.length) {
    return (
      <div className="bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-700 dark:text-purple-400" />
            {t('groups.relevantGroups.recommendedGroups')}
          </h3>
        </div>

        <div className="text-center py-8">
          <div className="w-16 h-16 bg-cream-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            {t('groups.relevantGroups.noGroupsForWatchlist')}
          </p>
          <button
            onClick={() => navigate('/create-group')}
            className="px-4 py-2 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 rounded-lg text-sm text-white transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('groups.relevantGroups.createAGroup')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-700/50 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-cream-300 dark:border-gray-700/50 bg-cream-100 dark:bg-gray-800/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-700 dark:text-purple-400" />
              {t('groups.relevantGroups.recommendedGroups')}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
              {activeStock
                ? t('groups.relevantGroups.groupsDiscussing', { ticker: activeStock })
                : t('groups.relevantGroups.basedOnWatchlist')}
              {activeStock && onActiveStockChange && (
                <button
                  onClick={() => onActiveStockChange(null)}
                  className="text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline underline-offset-2"
                >
                  {t('groups.relevantGroups.clear')}
                </button>
              )}
            </p>
          </div>
          <button
            onClick={handleViewAll}
            className="text-xs text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            {t('groups.relevantGroups.viewAll')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Groups List */}
      <div className="divide-y divide-cream-300 dark:divide-gray-700/50">
        {displayedGroups.map((group) => (
          <GroupRecommendationCard
            key={group.id}
            group={group}
            activeStock={activeStock}
            onClick={() => handleGroupClick(group.id)}
            onTickerClick={handleTickerClick}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 bg-cream-100 dark:bg-gray-800/20 border-t border-cream-300 dark:border-gray-700/50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {t('groups.relevantGroups.showingRelevantCount', { count: displayedGroups.length })}
          </span>
          <button
            onClick={() => navigate('/create-group')}
            className="text-xs text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3 h-3" />
            {t('groups.relevantGroups.createGroup')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * GROUP RECOMMENDATION CARD
 * Individual group item in the recommendation list
 */
const GroupRecommendationCard = ({ group, activeStock, onClick, onTickerClick }) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="p-4 hover:bg-cream-900/5 dark:hover:bg-gray-700/30 cursor-pointer transition-all duration-200 group"
    >
      <div className="flex items-start gap-3">
        {/* Group Avatar */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg">
            <span className="text-lg font-bold text-white">
              {group.name?.substring(0, 2).toUpperCase() || 'GR'}
            </span>
          </div>
        </div>

        {/* Group Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-500 dark:group-hover:text-blue-300 transition-colors">
              {group.name}
            </h4>
            
            {/* Privacy Badge */}
            {group.privacy && (
              <div className="flex-shrink-0">
                {group.privacy === 'public' ? (
                  <Globe className="w-3 h-3 text-green-400" title={t('groups.relevantGroups.publicGroupTitle')} />
                ) : (
                  <Lock className="w-3 h-3 text-yellow-400" title={t('groups.relevantGroups.privateGroupTitle')} />
                )}
              </div>
            )}

            {/* Joined Badge */}
            {group.joined && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded border border-blue-300 dark:border-blue-500/30">
                <Star className="w-3 h-3 fill-blue-300" />
                {t('groups.relevantGroups.joined')}
              </span>
            )}
          </div>

          {/* Stock Tickers */}
          {group.tickers?.length > 0 && (
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              {group.tickers.slice(0, 3).map((ticker) => (
                <button
                  key={ticker}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onTickerClick?.(ticker); }}
                  className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                    ticker === activeStock
                      ? 'bg-emerald-200 dark:bg-emerald-500/30 text-emerald-800 dark:text-emerald-200 border-emerald-400/50'
                      : 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/20 hover:bg-emerald-200 dark:hover:bg-emerald-500/20'
                  }`}
                  title={t('groups.relevantGroups.filterTickerTitle', { ticker })}
                >
                  ${ticker}
                </button>
              ))}
              {group.tickers.length > 3 && (
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {t('groups.relevantGroups.moreCount', { count: group.tickers.length - 3 })}
                </span>
              )}
            </div>
          )}

          {/* Group Stats */}
          <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span>{group.members || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              <span>{group.posts || 0}</span>
            </div>
            {group.activity && (
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-emerald-700 dark:text-emerald-400" />
                <span className="text-emerald-700 dark:text-emerald-400">{group.activity}</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0">
          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </div>
      </div>
    </div>
  );
};

export default RelevantGroupsRecommendation;